class Router {
  constructor() {
    this.preloader = document.getElementById('preloader');
    this.init();
  }

  init() {
    // Handle Initial Load Animation
    this.handleInitialLoad();

    // Intercept all clicks
    document.addEventListener('click', (e) => {
      // Bypassing JS Router in Customizer to allow standard page reloads
      if (window.Shopify && window.Shopify.designMode) return;

      const link = e.target.closest('a');
      if (!link) return;

      const url = link.href;

      // Ignore external links, anchors, mailto, tel, and strict-reload paths
      if (
        !url.startsWith(window.location.origin) ||
        url.includes('#') ||
        url.startsWith('mailto:') ||
        url.startsWith('tel:') ||
        this.shouldReload(url)
      ) {
        return;
      }

      e.preventDefault();
      this.navigate(url);
    });

    // Handle Back/Forward buttons
    window.addEventListener('popstate', () => {
      // Bypassing JS Router in Customizer
      if (window.Shopify && window.Shopify.designMode) return;
      this.navigate(window.location.href, false);
    });
  }

  shouldReload(url) {
    const reloadPaths = ['/checkout', '/account', '/challenge'];
    return reloadPaths.some(path => url.includes(path));
  }

  async handleInitialLoad() {
    if (this.preloader && window.getComputedStyle(this.preloader).display !== 'none') {
      if (!this.preloader.classList.contains('is-loading')) {
        this.preloader.classList.add('is-loading');
      }

      // 1. Start the counter animation (don't await yet)
      const counterPromise = this.animateCounter();

      // 2. Concurrently wait for assets (incl. all stylesheets to prevent CLS)
      const asyncTasks = [
        this.loadSecondaryAssets(),
        this.whenStylesheetsReady()
      ];
      const lcpImage = this.preloader.querySelector('.preloader-logo-ghost');
      if (lcpImage) {
        asyncTasks.push(lcpImage.decode().catch(() => { }));
      }

      // Wait for everything to be ready
      await Promise.all(asyncTasks);

      // 3. Fast-forward the counter if it's not done
      this.finishCounter = true;
      await counterPromise;

      // 4. Staggered Reveal to prevent TBT
      await this.revealSiteStaggered();

      this.togglePreloader(false);
    } else {
      this.loadSecondaryAssets();
      // No preloader (e.g. password page) — reveal content immediately
      this.revealContent();
    }
  }

  async revealSiteStaggered() {
    // Reveal Header first (Frame 1)
    const header = document.querySelector('.header-wrapper');
    if (header) header.classList.add('is-revealed');

    // Wait for the next frame (16ms) to split the layout work
    await new Promise(r => requestAnimationFrame(r));

    // Reveal main content and footer (Frame 2)
    const main = document.querySelector('.content-for-layout');
    const footer = document.querySelector('.footer-wrapper');
    if (main) main.classList.add('is-revealed');
    if (footer) footer.classList.add('is-revealed');
  }

  loadSecondaryAssets() {
    const assets = [];

    // Inject base.css
    if (window.baseCssUrl && !document.querySelector(`link[href="${window.baseCssUrl}"]`)) {
      assets.push(this.loadStyle(window.baseCssUrl));
    }

    // Inject header.css
    if (window.headerCssUrl && !document.querySelector(`link[href="${window.headerCssUrl}"]`)) {
      assets.push(this.loadStyle(window.headerCssUrl));
    }

    return Promise.all(assets);
  }

  revealContent() {
    const revealElements = ['.header-wrapper', '.content-for-layout', '.footer-wrapper'];
    revealElements.forEach(selector => {
      const el = document.querySelector(selector);
      if (el) el.classList.add('is-revealed');
    });
  }

  resetScroll() {
    // 1. Reset standard window
    window.scrollTo(0, 0);

    // 2. Reset Lenis if initialized
    if (window.lenis && typeof window.lenis.scrollTo === 'function') {
      window.lenis.scrollTo(0, { immediate: true });
    }

    // 3. Reset #MainContent container (often a scroll-lock container)
    const main = document.getElementById('MainContent');
    if (main) main.scrollTo(0, 0);
  }

  loadStyle(url) {
    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.onload = resolve;
      link.onerror = resolve; // Continue even on error
      document.head.appendChild(link);
    });
  }

  /**
   * Wait for all stylesheet <link> elements to finish loading.
   * Prevents CLS when preloader hides before section/template CSS has loaded.
   */
  whenStylesheetsReady() {
    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
    if (links.length === 0) return Promise.resolve();
    const timeout = 8000; // Max wait — avoid blocking forever
    return Promise.race([
      Promise.all(links.map(link => {
        return new Promise(resolve => {
          if (link.sheet && !link.sheet.disabled) {
            resolve();
            return;
          }
          const done = () => { resolve(); };
          link.addEventListener('load', done, { once: true });
          link.addEventListener('error', done, { once: true });
        });
      })),
      new Promise(r => setTimeout(r, timeout))
    ]);
  }

  async navigate(url, push = true) {
    if (this.isLoading) return;
    this.isLoading = true;

    // 1. Check Preloader Type
    // We check if the #preloader element exists and has a specific data attribute or just existence
    const useFullScreenLoader = this.preloader && window.getComputedStyle(this.preloader).display !== 'none';

    let counterPromise = Promise.resolve();

    if (useFullScreenLoader) {
      this.togglePreloader(true);
      counterPromise = this.animateCounter();
    } else {
      this.startProgressBar();
    }

    try {
      // 2. Fetch new content (concurrently with counter)
      const fetchPromise = fetch(url).then(res => res.text());

      // Wait for BOTH fetch and minimum counter animation
      const [html] = await Promise.all([fetchPromise, counterPromise]);

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // 3. CLS Prevention: Pre-load new stylesheets
      const newStyles = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
      const stylePromises = newStyles.map(link => {
        const href = link.getAttribute('href');
        if (!href || document.querySelector(`link[href="${href}"]`)) return Promise.resolve();
        return this.loadStyle(href);
      });
      if (stylePromises.length > 0) {
        await Promise.all(stylePromises);
        await this.whenStylesheetsReady();
      }

      // 4. Update DOM
      this.updateDOM(doc);

      // 4. Update History
      if (push) {
        window.history.pushState({}, '', url);
      }

      // 5. Sync Active Navigation Links (Move after history update to avoid lag)
      this.updateActiveLinks();

      // 6. Scroll to top
      this.resetScroll();

    } catch (error) {
      console.error('Navigation failed:', error);
      window.location.href = url; // Fallback to full reload
    } finally {
      // 6. Final UI revealing
      if (useFullScreenLoader) {
        this.togglePreloader(false);
      } else {
        this.finishProgressBar();
        this.revealContent(); // Ensure content is visible even with progress bar navigation
      }
      this.isLoading = false;
    }
  }

  togglePreloader(show) {
    if (!this.preloader) return;

    if (show) {
      this.preloader.classList.add('is-loading');
      this.preloader.classList.remove('is-hidden');
    } else {
      this.preloader.classList.remove('is-loading');
      this.preloader.classList.add('is-hidden');

      // Reveal site content atomically
      const revealElements = ['.header-wrapper', '.content-for-layout', '.footer-wrapper'];
      revealElements.forEach(selector => {
        const el = document.querySelector(selector);
        if (el) el.classList.add('is-revealed');
      });

      // Reset counter after valid transition out
      setTimeout(() => {
        const counter = this.preloader.querySelector('.preloader-counter');
        if (counter) counter.innerText = '0%';
      }, 500);
    }
  }

  animateCounter() {
    return new Promise(resolve => {
      const counterEl = this.preloader.querySelector('.preloader-counter');
      if (!counterEl) {
        setTimeout(resolve, 800);
        return;
      }

      let start = null;
      const duration = 1000; // 1 second min duration

      const step = (timestamp) => {
        if (!start) start = timestamp;
        const progressTime = timestamp - start;

        let percentage;
        if (this.finishCounter) {
          // Accelerate to 100% if signal received
          percentage = 100;
        } else {
          percentage = Math.min((progressTime / duration) * 100, 100);
        }

        // Update DOM
        counterEl.innerText = Math.floor(percentage) + '%';
        this.preloader.style.setProperty('--preloader-progress', percentage / 100);

        if (percentage < 100) {
          window.requestAnimationFrame(step);
        } else {
          // Ensure final state
          counterEl.innerText = '100%';
          this.preloader.style.setProperty('--preloader-progress', 1);
          this.finishCounter = false; // Reset for next use
          resolve();
        }
      };

      window.requestAnimationFrame(step);
    });
  }

  // NProgress Style Top Bar Logic
  createProgressBar() {
    let bar = document.getElementById('nprogress-bar');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'nprogress-bar';
      Object.assign(bar.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        height: '2px',
        backgroundColor: 'rgba(var(--color-foreground), 1)', // High contrast against background
        zIndex: '2147483647', // Max z-index
        transition: 'width 0.3s ease, opacity 0.3s ease',
        width: '0%',
        opacity: '0',
        pointerEvents: 'none',
        boxShadow: '0 0 10px rgba(var(--color-foreground), 0.5)' // Glow for visibility
      });
      document.body.appendChild(bar);
    }
    return bar;
  }

  startProgressBar() {
    const bar = this.createProgressBar();
    bar.style.transition = 'none';
    bar.style.width = '0%';
    bar.style.opacity = '1';
    
    // Force reflow
    bar.offsetWidth;
    
    bar.style.transition = 'width 0.4s ease, opacity 0.4s ease';
    bar.style.width = '2%';

    // Trickle animation
    let width = 2;
    this.progressInterval = setInterval(() => {
      if (width >= 90) return;
      width += (Math.random() * 5);
      bar.style.width = width + '%';
    }, 400);
  }

  finishProgressBar() {
    if (this.progressInterval) clearInterval(this.progressInterval);
    const bar = document.getElementById('nprogress-bar');
    if (bar) {
      bar.style.transition = 'width 0.3s ease-out, opacity 0.3s ease-in';
      bar.style.width = '100%';
      
      // Delay fading out so the user sees it reach 100%
      setTimeout(() => {
        bar.style.opacity = '0';
        // Reset after it's fully faded
        setTimeout(() => {
          bar.style.width = '0%';
        }, 400);
      }, 500);
    }
  }

  updateDOM(doc) {
    // Replace Main Content
    const newContent = doc.querySelector('#MainContent');
    const currentContent = document.getElementById('MainContent');
    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML;
      // Re-assign classes if needed (e.g. template specific classes on main)
      currentContent.className = newContent.className;
    }

    // Update Title
    document.title = doc.title;

    // Update Body Classes (often used for page-specific styles)
    document.body.className = doc.body.className;

    // Sync Header and Footer classes + data-sticky to prevent template-specific states from leaking
    const newHeaderRoot = doc.querySelector('header-root');
    const currentHeaderRoot = document.querySelector('header-root');
    if (newHeaderRoot && currentHeaderRoot) {
      currentHeaderRoot.className = newHeaderRoot.className;
      if (newHeaderRoot.dataset.sticky !== undefined) {
        currentHeaderRoot.dataset.sticky = newHeaderRoot.dataset.sticky;
      }
    }

    const newFooterRoot = doc.querySelector('footer-root');
    const currentFooterRoot = document.querySelector('footer-root');
    if (newFooterRoot && currentFooterRoot) {
      currentFooterRoot.className = newFooterRoot.className;
    }

    // Execute inline scripts in the new content
    // Note: Custom Elements automatically handle their own lifecycle
    // But standard inline <script> tags inside #MainContent won't execute by default when set via innerHTML.
    this.runScripts(currentContent);

    // Dispatch event for other listeners
    window.dispatchEvent(new Event('page:loaded'));

    // Destroy and re-init Lenis so the new content gets a fresh instance
    // with clean ResizeObservers — no stale state from the previous page.
    if (window.initLenis) {
      requestAnimationFrame(() => {
        window.initLenis();
      });
    }
  }

  runScripts(container) {
    const scripts = container.querySelectorAll('script');
    scripts.forEach(oldScript => {
      // Skip scripts that have a src attribute and are already present in the document
      // to prevent re-declaration errors (e.g. for classes or consts)
      if (oldScript.src && document.querySelector(`script[src="${oldScript.src}"]`)) {
        return;
      }

      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  updateActiveLinks() {
    const currentPath = window.location.pathname;
    // Target all primary navigation links across header, footer, and mobile drawer
    const links = document.querySelectorAll([
      '.header-wrapper a',
      '.footer-wrapper a',
      '.mobile-menu-overlay a'
    ].join(','));

    links.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) return;

      // Split the current path and href to handle potential query params or hashes (though link intercepts usually avoid these)
      const cleanHref = href.split('?')[0].split('#')[0];
      const cleanPath = currentPath.split('?')[0].split('#')[0];

      // 1. Skip Logo Wrappers (Logo shouldn't have active highlight)
      if (link.classList.contains('header__heading-logo-wrapper') ||
        link.classList.contains('header__heading-link')) {
        return;
      }

      // Direct exact match for current page
      const isActive = cleanHref === cleanPath;

      if (isActive) {
        link.classList.add('list-menu__item--active');
        link.setAttribute('aria-current', 'page');
      } else {
        link.classList.remove('list-menu__item--active');
        link.removeAttribute('aria-current');
      }
    });
  }

  wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Initialize Router
document.addEventListener('DOMContentLoaded', () => {
  window.router = new Router();
});
